// views/adminView/adminPickupsView/adminPickupView.jsx
// Remake de look & feel (alineado a Pedidos). La lógica de datos NO cambia:
// mismas llamadas al store (fetchPickupsDateRange, fetchClients) y mismo shape.

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Search,
  Calendar,
  FileText,
  Package,
  Building2,
  UserX,
  Truck,
  ChevronRight,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAuth from "../../../hooks/useAuth";
import useRoutes from "../../../store/routes";
import { getDeliveryUsers } from "../../../api/user";
import { getPackagesByCollect } from "../../../api/packages";
import { packageStatus, packageStatusStyles } from "../../../utils/packageStatus";
import { originLabel } from "../../../components/OriginIcon";
import useIsMobile from "../../../hooks/useIsMobile";
import SearchableSelect from "../../../components/ui/SearchableSelect";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Skeleton } from "../../../components/ui/Skeleton";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import { useUrlState, useUrlStateBatch } from "../../../hooks/useUrlState";
import {
  COLLECT_TYPE,
  COLLECT_TYPE_LABEL,
  collectTypeLabel,
  collectTypeModifier,
  resolveCollectType,
} from "./collectType";
import "./adminPickupView.scss";

const MONTH = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function formatFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()} · ${hh}:${mm}`;
}
function iniciales(a, b) {
  return `${(a || "").charAt(0)}${(b || "").charAt(0)}`.toUpperCase() || "?";
}

// YYYY-MM-DD para inputs type="date" (mismo shape que ya consume el store).
function toInputDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Presets ofrecidos por el DateRangeFilter (solo derivan startDate/endDate;
// no cambian el endpoint).
const PICKUP_PRESETS = [
  { key: "today", quick: true },
  { key: "7d", quick: true },
  { key: "30d", quick: true },
  { key: "thisMonth", quick: true },
];

// Rango por defecto: últimos 7 días alineados al preset '7d' (hoy − 6 → hoy).
// Coincide con el chip '7d' del DateRangeFilter para resaltarlo y mostrar el
// resumen al cargar sin params en la URL.
function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return { startDate: toInputDate(start), endDate: toInputDate(end) };
}

const DEFAULT_RANGE = defaultRange();

export default function AdminPickupsView() {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Filtros persistidos en la URL. Sin params ⇒ rango/clientes idénticos al
  // comportamiento previo (últimos 7 días, todas las empresas).
  // Lectura de cada fecha; la ESCRITURA de ambas se hace JUNTA con useUrlStateBatch.
  // Con dos setters separados (setStartDate + setEndDate) las navegaciones se
  // pisaban: react-router no encadena dos setSearchParams sincronos, asi que la 2da
  // (to) no veia el cambio de la 1ra (from) y solo se guardaba `to` → el filtro de
  // fechas quedaba con el `from` pegado en el default. (Bug de la "estandarizacion".)
  const [startDate] = useUrlState("from", DEFAULT_RANGE.startDate);
  const [endDate] = useUrlState("to", DEFAULT_RANGE.endDate);
  const setDateRange = useUrlStateBatch({
    from: { type: "string", defaultValue: DEFAULT_RANGE.startDate },
    to: { type: "string", defaultValue: DEFAULT_RANGE.endDate },
  });
  const [clientId, setClientId] = useUrlState("client", "");
  // Tipo de colecta. Se filtra en memoria y no en el backend a propósito: el endpoint
  // ya devolvió el rango completo, así que agregar un parámetro obligaría a re-consultar
  // para algo que se resuelve sobre los datos que ya están en pantalla.
  const [collectTypeFilter, setCollectTypeFilter] = useUrlState("tipo", "");
  // Repartidor. A diferencia del tipo de colecta, éste SÍ va al backend: filtrar en
  // memoria mostraría "0 retiros" cada vez que los del repartidor caen fuera del bloque
  // ya traído, y el endpoint tiene índice por (operador, assignTo, fecha).
  const [driverId, setDriverId] = useUrlState("driver", "");
  const didAutoSearch = useRef(false);
  const [pickups, setPickups] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Error of the LAST search: null = no error; { message } holds the backend
  // text when it sent one (null = nothing usable, the generic text is shown).
  // A failure is never rendered as "no results": they are different states.
  const [searchError, setSearchError] = useState(null);
  // Paginación client-side: no montamos cientos de filas de golpe (los KPIs y
  // el contador siguen sobre el total). Se renderiza en bloques de PAGE_SIZE.
  const PAGE_SIZE = 40;
  const [visible, setVisible] = useState(PAGE_SIZE);

  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);

  // Detalle de un retiro: qué paquetes trajo y en qué estado están HOY. Se pide al
  // expandir y se cachea por retiro, indexado por `_id` del pickup. Cerrar y volver a
  // abrir no vuelve a pedir.
  const [expandedId, setExpandedId] = useState(null);
  const [detalles, setDetalles] = useState({});

  // ── Store (sin cambios) ──
  const fetchPickupsDateRange = useRoutes((state) => state.fetchPickupsDateRange);
  const fetchClientsFromStore = useRoutes((state) => state.fetchClients);
  const clients = useRoutes((state) => state.clients);

  useEffect(() => {
    const logisticCompanyId = user?.logisticCompany?._id;
    if (logisticCompanyId) {
      setIsLoadingClient(true);
      fetchClientsFromStore(logisticCompanyId).finally(() => setIsLoadingClient(false));
    }
  }, [user, fetchClientsFromStore]);

  useEffect(() => {
    const mapped = clients.map((c) => ({ value: c._id, label: c.name || t("Sin nombre") }));
    setClientOptions([{ value: "", label: t("Todas las empresas") }, ...mapped]);
  }, [clients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Repartidores activos del operador para el selector. Misma fuente que el resto del
  // admin (`getDeliveryUsers`), que devuelve el plantel completo y no sólo quienes
  // tuvieron actividad en el rango: filtrar por alguien que no retiró nada tiene que
  // devolver "sin retiros", no esconder su nombre de la lista.
  useEffect(() => {
    const logisticCompanyId = user?.logisticCompany?._id;
    if (!logisticCompanyId) return;
    let cancelled = false;
    setIsLoadingDrivers(true);
    getDeliveryUsers("active", "delivery", logisticCompanyId)
      .then((result) => {
        if (cancelled) return;
        const users = result?.docs || result?.data?.docs || (Array.isArray(result) ? result : []);
        const mapped = (Array.isArray(users) ? users : [])
          .filter((u) => u && u._id)
          .map((u) => ({
            value: u._id,
            label: [u.name, u.lastName].filter(Boolean).join(" ") || u.email || u._id,
          }));
        setDriverOptions([{ value: "", label: t("Todos los repartidores") }, ...mapped]);
      })
      .catch(() => {
        if (!cancelled) setDriverOptions([{ value: "", label: t("Todos los repartidores") }]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDrivers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ejecuta la búsqueda con un rango explícito (evita estado obsoleto al usar
  // los chips o el auto-search inicial). Misma llamada al store, sin cambios.
  const runSearch = async (sd, ed) => {
    if (!sd || !ed) return;
    setSearchError(null);
    setSearching(true);
    setHasSearched(true);
    setVisible(PAGE_SIZE);
    // El detalle cacheado pertenece al resultado anterior: si no se limpia, una fila
    // nueva podría heredar el detalle de la que ocupaba esa posición.
    setExpandedId(null);
    setDetalles({});
    // Inverted range: the backend rejects it with 400. Warn here, no request.
    if (sd > ed) {
      setSearching(false);
      setPickups([]);
      setSearchError({ message: t("La fecha desde no puede ser posterior a la fecha hasta.") });
      return;
    }
    try {
      const logisticCompanyId = user?.logisticCompany?._id || null;
      const data = await fetchPickupsDateRange(sd, ed, logisticCompanyId, clientId, driverId);
      if (data?.ok) {
        setPickups(data.data || []);
      } else {
        // The store already hands back the backend text (or null when none usable).
        setPickups([]);
        setSearchError({
          message: typeof data?.error === "string" && data.error.trim() ? data.error.trim() : null,
        });
      }
    } catch (error) {
      setPickups([]);
      setSearchError({ message: null });
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(startDate, endDate);
  };

  // Abre/cierra el detalle de un retiro y lo pide la primera vez.
  //
  // Un retiro sin `collectId` es anterior al despliegue del vínculo paquete↔retiro. En
  // ese caso NO se reconstruye el detalle cruzando por fecha + comercio: ese cruce
  // atribuye mal cuando hay dos retiros al mismo comercio el mismo día, que es
  // exactamente el caso en el que alguien abriría esta fila. Se dice que no está.
  const toggleDetalle = useCallback(
    async (pickup) => {
      const id = pickup._id;
      if (expandedId === id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(id);

      if (!pickup.collectId || detalles[id]) return;

      setDetalles((prev) => ({ ...prev, [id]: { loading: true } }));
      try {
        const items = await getPackagesByCollect(pickup.collectId);
        setDetalles((prev) => ({ ...prev, [id]: { loading: false, items } }));
      } catch (error) {
        setDetalles((prev) => ({
          ...prev,
          [id]: { loading: false, error: error?.response?.status === 403 ? "forbidden" : "error" },
        }));
      }
    },
    [expandedId, detalles],
  );

  // Cambio de rango desde el DateRangeFilter (preset o personalizado). Solo
  // actualiza startDate/endDate; la consulta se dispara con "Buscar retiros".
  const handleRangeChange = (sd, ed) => {
    setDateRange({ from: sd, to: ed }); // una sola navegacion, ambas fechas, sin pisarse
  };

  // Al entrar se muestran datos sin tipear fechas (rango sembrado desde la URL
  // o el default histórico de 7 días).
  useEffect(() => {
    if (isLoading || didAutoSearch.current) return;
    didAutoSearch.current = true;
    runSearch(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Preset activo para resaltar el chip correspondiente (o "custom").
  const datePreset = useMemo(() => {
    const match = PICKUP_PRESETS.find(({ key }) => {
      const [s, e] = rangeFromPreset(key);
      return s.format("YYYY-MM-DD") === startDate && e.format("YYYY-MM-DD") === endDate;
    });
    return match ? match.key : "custom";
  }, [startDate, endDate]);

  // Resultado ya filtrado por tipo. Es lo que se pinta y lo que cuentan los KPIs: si
  // los indicadores siguieran sobre el total, dirían una cosa y la tabla otra.
  const visiblePickups = useMemo(() => {
    if (!collectTypeFilter) return pickups;
    return pickups.filter((p) => resolveCollectType(p) === collectTypeFilter);
  }, [pickups, collectTypeFilter]);

  // Al cambiar el filtro se vuelve al primer bloque: si el usuario había paginado y
  // el nuevo subconjunto es más chico, quedaría mostrando de más.
  useEffect(() => {
    setVisible(PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectTypeFilter]);

  // KPIs derivados del resultado en memoria.
  const kpis = useMemo(() => {
    const total = visiblePickups.length;
    let withPdf = 0;
    let sinRepartidor = 0;
    const empresas = new Set();
    visiblePickups.forEach((p) => {
      if (p.pdfUrl) withPdf += 1;
      if (!(p.assignTo && typeof p.assignTo === "object")) sinRepartidor += 1;
      const name = p.client && typeof p.client === "object" ? p.client.name : p.client;
      if (name) empresas.add(name);
    });
    return [
      { label: t("Retiros"), value: total, icon: Package },
      { label: t("Empresas"), value: empresas.size, icon: Building2 },
      { label: t("Con PDF"), value: withPdf, icon: FileText },
      { label: t("Sin repartidor"), value: sinRepartidor, icon: UserX },
    ];
  }, [visiblePickups, t]);

  if (isLoading) {
    return (
      <div className="pk">
        <div className="pk-card pk-card--pad pk-skel">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <TableSkeleton rows={6} columns={["w-20", "w-40", "w-32", "w-40", "w-32", "w-16"]} />
      </div>
    );
  }

  // ── Detalle de un retiro: qué paquetes trajo y en qué estado están HOY ──
  const DetalleRetiro = ({ pickup, estado }) => {
    // Retiro anterior al despliegue del vínculo. Se dice que no está en vez de
    // reconstruirlo cruzando por fecha + comercio: ese cruce atribuye mal justo cuando
    // hay dos retiros al mismo comercio el mismo día, que es cuando alguien abriría esto.
    if (!pickup.collectId) {
      return (
        <div className="pk-detail">
          <p className="pk-detail__note">
            <AlertCircle size={14} />
            {t("Este retiro no tiene registrado el vínculo con sus paquetes. El remito sigue siendo la fuente de qué se retiró ese día.")}
          </p>
        </div>
      );
    }

    if (!estado || estado.loading) {
      return <TableSkeleton rows={3} columns={["w-32", "w-40", "w-24", "w-32"]} />;
    }

    if (estado.error) {
      return (
        <div className="pk-detail">
          <p className="pk-detail__note">
            <AlertCircle size={14} />
            {estado.error === "forbidden"
              ? t("No tienes permiso para ver los paquetes de este retiro.")
              : t("No se pudo cargar el detalle de este retiro.")}
          </p>
        </div>
      );
    }

    const items = estado.items || [];

    if (items.length === 0) {
      return (
        <div className="pk-detail">
          <p className="pk-detail__note">
            <AlertCircle size={14} />
            {t("No hay paquetes asociados a este retiro.")}
          </p>
        </div>
      );
    }

    return (
      <div className="pk-detail">
        <div className="pk-detail__title">
          {items.length} {items.length !== 1 ? t("paquetes") : t("paquete")}
        </div>
        <div className="pk-detail__table" role="table" aria-label={t("Paquetes del retiro")}>
          <div className="pk-detail__head" role="row">
            <div className="pk-detail__cell" role="columnheader">{t("N° de seguimiento")}</div>
            <div className="pk-detail__cell" role="columnheader">{t("Origen del pedido")}</div>
            <div className="pk-detail__cell" role="columnheader">{t("Estado actual")}</div>
            <div className="pk-detail__cell" role="columnheader">{t("Destino")}</div>
          </div>
          {items.map((pkg) => (
            <div className="pk-detail__row" role="row" key={pkg._id}>
              <div className="pk-detail__cell" role="cell">
                <span className="pk-mono">{pkg.followingNumber ?? "—"}</span>
              </div>
              <div className="pk-detail__cell" role="cell">
                {/* Etiqueta canónica del origen, no el `type` crudo: los pedidos creados
                    a mano viven en la base como "OTS" y nunca deben mostrarse así.
                    `originLabel` además normaliza el casing —"MERCADOLIBRE" y
                    "MercadoLibre" conviven en la colección— y escribe bien los nombres
                    de marca. Se usa el catálogo compartido en vez de un mapa local
                    porque el repo ya arrastra cuatro duplicados de esto. */}
                <span className="pk-cell-muted">{originLabel(pkg.type, { fallback: "—" })}</span>
              </div>
              <div className="pk-detail__cell" role="cell">
                <span className={`pk-status ${packageStatusStyles[pkg.status] || ""}`}>
                  {t(packageStatus[pkg.status] || "Sin estado")}
                </span>
              </div>
              <div className="pk-detail__cell" role="cell">
                <span className="pk-cell-muted">
                  {pkg.shipping_address?.municipality?.name || "—"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Card de un retiro para vista mobile ──
  const PickupCard = ({ p }) => (
    <div className="pk-mcard">
      <div className="pk-mcard__top">
        <span className="pk-mono">#{p.correlative}</span>
        {p.pdfUrl ? (
          <a className="pk-link" href={p.pdfUrl} target="_blank" rel="noreferrer">
            <FileText size={14} /> {t("Ver PDF")}
          </a>
        ) : (
          <span className="pk-cell-muted">—</span>
        )}
      </div>
      <div className="pk-mcard__body">
        <div className="pk-mrow">
          <span className="pk-mrow__k">{t("Empresa")}</span>
          <span className="pk-mrow__v">
            {p.client && typeof p.client === "object" ? (
              <>
                <span className="pk-cell-strong">{p.client.name}</span>
                {p.client?.fiscalAddress && <span className="pk-cell-muted">{p.client.fiscalAddress}</span>}
              </>
            ) : (
              <span className="pk-cell-muted">{p.client || "—"}</span>
            )}
          </span>
        </div>
        <div className="pk-mrow">
          <span className="pk-mrow__k">{t("Entregado por")}</span>
          <span className="pk-mrow__v">
            {p.dispatcher ? (
              <>
                <span className="pk-cell-strong">{p.dispatcher.fullName}</span>
                {p.dispatcher.rut && <span className="pk-cell-muted">{p.dispatcher.rut}</span>}
              </>
            ) : (
              <span className="pk-cell-muted">—</span>
            )}
          </span>
        </div>
        <div className="pk-mrow">
          <span className="pk-mrow__k">{t("Repartidor")}</span>
          <span className="pk-mrow__v">
            {p.assignTo && typeof p.assignTo === "object" ? (
              <span className="pk-user">
                <span className="pk-avatar">{iniciales(p.assignTo.name, p.assignTo.lastName)}</span>
                <span className="pk-user__info">
                  <span className="pk-cell-strong">{p.assignTo.name} {p.assignTo.lastName}</span>
                  {p.assignTo.email && <span className="pk-cell-muted">{p.assignTo.email}</span>}
                </span>
              </span>
            ) : (
              <span className="pk-cell-muted">—</span>
            )}
          </span>
        </div>
        <div className="pk-mrow">
          <span className="pk-mrow__k">{t("Tipo de retiro")}</span>
          <span className="pk-mrow__v">
            <span
              className={`pk-type pk-type--${collectTypeModifier(p)}`}
            >
              {t(collectTypeLabel(p))}
            </span>
          </span>
        </div>
        <div className="pk-mrow">
          <span className="pk-mrow__k">{t("Fecha retiro")}</span>
          <span className="pk-mrow__v"><span className="pk-cell-muted">{formatFecha(p.date)}</span></span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pk">
      {/* ── Header ── */}
      <PageHeader
        icon={<Package className="w-6 h-6 text-fg-muted" />}
        title={t("Retiros realizados")}
        subtitle={t("Consulta los retiros ejecutados por rango de fecha y empresa.")}
        actions={
          hasSearched && !searching && !searchError ? (
            <span className="pk-count">
              <Package size={16} />
              <strong>{visiblePickups.length}</strong> {visiblePickups.length !== 1 ? t("retiros") : t("retiro")}
            </span>
          ) : null
        }
      />

      {/* ── KPIs derivados del resultado ── */}
      {hasSearched && !searching && visiblePickups.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {kpis.map(({ label, value, icon: Icon }) => (
            <div key={label} className="pk-card flex items-center gap-3 px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-muted text-fg-muted">
                <Icon size={16} />
              </span>
              <span className="min-w-0">
                <span className="block text-lg font-semibold leading-none text-fg">{value}</span>
                <span className="block truncate text-xs text-fg-muted">{label}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Filtros ── */}
      <form className="pk-card pk-filters" onSubmit={handleSubmit}>
        <div className="pk-field pk-field--grow">
          <label><Calendar size={14} /> {t("Rango de fechas")}</label>
          <DateRangeFilter
            compact
            value={datePreset}
            onChange={handleRangeChange}
            presets={PICKUP_PRESETS}
            customRange={{ start: startDate, end: endDate }}
          />
        </div>
        {user?.logisticCompany?._id && (
          <div className="pk-field pk-field--grow">
            <label><Building2 size={14} /> {t("Empresa")}</label>
            <SearchableSelect
              options={clientOptions}
              value={clientId}
              onChange={(value) => setClientId(value)}
              placeholder={isLoadingClient ? t("Cargando empresas…") : t("Todas las empresas")}
              isClearable
            />
          </div>
        )}
        {user?.logisticCompany?._id && (
          <div className="pk-field pk-field--grow">
            <label><Truck size={14} /> {t("Repartidor")}</label>
            <SearchableSelect
              options={driverOptions}
              value={driverId}
              onChange={(value) => setDriverId(value || "")}
              placeholder={isLoadingDrivers ? t("Cargando repartidores…") : t("Todos los repartidores")}
              isClearable
            />
          </div>
        )}
        <div className="pk-field pk-field--grow">
          <label><Package size={14} /> {t("Tipo de retiro")}</label>
          <SearchableSelect
            options={[
              { value: "", label: t("Todos") },
              { value: COLLECT_TYPE.PICKUP, label: t(COLLECT_TYPE_LABEL[COLLECT_TYPE.PICKUP]) },
              {
                value: COLLECT_TYPE.PICKUP_PICK_AND_GO,
                label: t(COLLECT_TYPE_LABEL[COLLECT_TYPE.PICKUP_PICK_AND_GO]),
              },
              { value: COLLECT_TYPE.RECEPTION, label: t(COLLECT_TYPE_LABEL[COLLECT_TYPE.RECEPTION]) },
            ]}
            value={collectTypeFilter}
            onChange={(value) => setCollectTypeFilter(value || "")}
            placeholder={t("Todos")}
            isClearable
          />
        </div>
        <button type="submit" className="pk-btn pk-btn--primary" disabled={searching}>
          {searching ? (
            <span className="pk-btn__spinner" aria-hidden />
          ) : (
            <Search size={16} />
          )}
          {searching ? t("Buscando…") : t("Buscar retiros")}
        </button>
      </form>

      {/* ── Resultados ── */}
      {searching ? (
        <TableSkeleton rows={6} columns={["w-20", "w-40", "w-32", "w-40", "w-32", "w-16"]} />
      ) : !hasSearched ? (
        <div className="pk-card">
          <EmptyState
            icon={<Search />}
            title={t("Elige un rango de fechas")}
            description={t("Selecciona desde / hasta (y opcionalmente una empresa) y presiona “Buscar retiros”.")}
          />
        </div>
      ) : searchError ? (
        <div className="pk-card">
          <div role="alert" className="flex flex-col items-center justify-center text-center py-12 px-6">
            <div className="mb-4 flex items-center justify-center w-12 h-12 rounded-full bg-status-error-soft text-status-error">
              <AlertCircle size={24} aria-hidden />
            </div>
            <h3 className="text-base font-semibold text-status-error">
              {t("No se pudieron cargar los retiros.")}
            </h3>
            {/* Backend text is already Spanish: it is NOT wrapped in t(). */}
            <p className="mt-1 max-w-md text-pretty text-sm text-fg-muted">
              {searchError.message ||
                t("Ocurrió un problema al consultar los retiros. Intenta nuevamente.")}
            </p>
            <button type="button" className="pk-btn mt-4" onClick={() => runSearch(startDate, endDate)}>
              {t("Reintentar")}
            </button>
          </div>
        </div>
      ) : visiblePickups.length === 0 ? (
        <div className="pk-card">
          <EmptyState
            icon={<Package />}
            title={t("Sin retiros en el rango")}
            description={t("No se encontraron retiros para los filtros seleccionados.")}
          />
        </div>
      ) : isMobile ? (
        <div className="pk-mobile-cards">
          {visiblePickups.slice(0, visible).map((p) => (
            <PickupCard key={p._id} p={p} />
          ))}
          {visiblePickups.length > visible && (
            <button
              type="button"
              className="pk-btn"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
            >
              {t("Cargar más")} ({visiblePickups.length - visible})
            </button>
          )}
        </div>
      ) : (
        <div className="pk-card">
          <div className="pk-list" role="table" aria-label={t("Retiros realizados")}>
            <div className="pk-list__head" role="row">
              <div className="pk-list__hcell" role="columnheader">
                <span className="sr-only">{t("Detalle")}</span>
              </div>
              <div className="pk-list__hcell" role="columnheader">{t("N° remito")}</div>
              <div className="pk-list__hcell" role="columnheader">{t("Empresa")}</div>
              <div className="pk-list__hcell" role="columnheader">{t("Entregado por")}</div>
              <div className="pk-list__hcell" role="columnheader">{t("Repartidor")}</div>
              <div className="pk-list__hcell" role="columnheader">{t("Tipo de retiro")}</div>
              <div className="pk-list__hcell" role="columnheader">{t("Fecha retiro")}</div>
              <div className="pk-list__hcell pk-list__hcell--right" role="columnheader">{t("PDF")}</div>
            </div>
            {visiblePickups.slice(0, visible).map((p) => (
              <div role="rowgroup" key={p._id}>
              <div className="pk-list__row" role="row">
                <div className="pk-list__cell" role="cell">
                  {/* El botón va DENTRO de la celda, no sobre la fila: un role="button"
                      envolviendo la fila borra su contenido del árbol de accesibilidad. */}
                  <button
                    type="button"
                    className="pk-toggle"
                    onClick={() => toggleDetalle(p)}
                    aria-expanded={expandedId === p._id}
                    aria-label={
                      expandedId === p._id
                        ? t("Ocultar los paquetes de este retiro")
                        : t("Ver los paquetes de este retiro")
                    }
                  >
                    {expandedId === p._id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
                <div className="pk-list__cell" role="cell">
                  <span className="pk-mono">#{p.correlative}</span>
                </div>
                <div className="pk-list__cell" role="cell">
                  {p.client && typeof p.client === "object" ? (
                    <div className="pk-cell-strong">{p.client.name}</div>
                  ) : (
                    <span className="pk-cell-muted">{p.client || "—"}</span>
                  )}
                  {p.client?.fiscalAddress && <div className="pk-cell-muted">{p.client.fiscalAddress}</div>}
                </div>
                <div className="pk-list__cell" role="cell">
                  {p.dispatcher ? (
                    <>
                      <div className="pk-cell-strong">{p.dispatcher.fullName}</div>
                      {p.dispatcher.rut && <div className="pk-cell-muted">{p.dispatcher.rut}</div>}
                    </>
                  ) : (
                    <span className="pk-cell-muted">—</span>
                  )}
                </div>
                <div className="pk-list__cell" role="cell">
                  {p.assignTo && typeof p.assignTo === "object" ? (
                    <div className="pk-user">
                      <span className="pk-avatar">{iniciales(p.assignTo.name, p.assignTo.lastName)}</span>
                      <div className="pk-user__info">
                        <div className="pk-cell-strong">{p.assignTo.name} {p.assignTo.lastName}</div>
                        {p.assignTo.email && <div className="pk-cell-muted">{p.assignTo.email}</div>}
                      </div>
                    </div>
                  ) : (
                    <span className="pk-cell-muted">—</span>
                  )}
                </div>
                <div className="pk-list__cell" role="cell">
                  <span
                    className={`pk-type pk-type--${collectTypeModifier(p)}`}
                  >
                    {t(collectTypeLabel(p))}
                  </span>
                </div>
                <div className="pk-list__cell" role="cell">
                  <span className="pk-cell-muted">{formatFecha(p.date)}</span>
                </div>
                <div className="pk-list__cell pk-list__cell--right" role="cell">
                  {p.pdfUrl ? (
                    <a className="pk-link" href={p.pdfUrl} target="_blank" rel="noreferrer">
                      <FileText size={14} /> {t("Ver PDF")}
                    </a>
                  ) : (
                    <span className="pk-cell-muted">—</span>
                  )}
                </div>
              </div>
              {expandedId === p._id && (
                <div className="pk-list__detail" role="row">
                  <div role="cell" aria-colspan={8}>
                    <DetalleRetiro pickup={p} estado={detalles[p._id]} />
                  </div>
                </div>
              )}
              </div>
            ))}
          </div>
          {visiblePickups.length > visible && (
            <div style={{ display: "flex", justifyContent: "center", padding: "0.75rem" }}>
              <button
                type="button"
                className="pk-btn"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
              >
                {t("Cargar más")} ({visiblePickups.length - visible})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
